import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  Check,
  X,
  Sparkles,
  Gift,
  ShieldCheck,
  Zap,
  TrendingUp,
  Mic,
  CreditCard,
  ArrowRight,
  Clock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import FinlyLogo from "@/components/FinlyLogo";
import FinlyLogoMark from "@/components/FinlyLogoMark";

const API = process.env.REACT_APP_BACKEND_URL;

// Promo deadline: end of the current month (CDMX). The countdown rolls naturally
// month-to-month so the offer keeps feeling "ending soon" without manual edits.
function getPromoDeadline() {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth(); // 0-indexed
  // Last day of current month, 23:59:59 local time
  return new Date(year, month + 1, 0, 23, 59, 59).getTime();
}

function useCountdown(targetMs) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const remaining = Math.max(0, targetMs - now);
  const days = Math.floor(remaining / (1000 * 60 * 60 * 24));
  const hours = Math.floor((remaining / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((remaining / (1000 * 60)) % 60);
  const seconds = Math.floor((remaining / 1000) % 60);
  return { days, hours, minutes, seconds, expired: remaining === 0 };
}

const FEATURES = [
  { label: "Captura por voz (Whisper AI)", free: "5 al día", premium: "Ilimitado", icon: Mic },
  { label: "Cuentas (efectivo, débito, crédito)", free: "Hasta 3", premium: "Ilimitadas", icon: CreditCard },
  { label: "Salud por cuenta en Dashboard", free: true, premium: true, icon: ShieldCheck },
  { label: "OCR de tickets (Gemini Vision)", free: false, premium: true, icon: Sparkles },
  { label: "Plan de pago inteligente", free: false, premium: true, icon: TrendingUp, highlight: true },
  { label: "Proyección de intereses ahorrados", free: false, premium: true, icon: Zap, highlight: true },
  { label: "Comparativas mes vs mes", free: true, premium: true, icon: TrendingUp },
  { label: "Exportar a CSV/PDF", free: "Mes actual", premium: "Histórico completo" },
  { label: "Soporte por email", free: "Comunidad", premium: "Prioritario < 24h" },
];

function FeatureCell({ value }) {
  if (value === true)
    return (
      <div className="flex items-center justify-center" data-testid="cell-check">
        <Check className="w-4 h-4 text-[#00C97D]" strokeWidth={3} />
      </div>
    );
  if (value === false)
    return (
      <div className="flex items-center justify-center" data-testid="cell-x">
        <X className="w-4 h-4 text-[#475569]" strokeWidth={2.5} />
      </div>
    );
  return (
    <div className="text-center text-[13px] text-[#CBD5E1]" data-testid="cell-text">
      {value}
    </div>
  );
}

// PostHog event tracking helper. Safely no-ops if PostHog isn't loaded yet.
function trackCta(location, refCode) {
  try {
    if (typeof window !== "undefined" && window.posthog && typeof window.posthog.capture === "function") {
      window.posthog.capture("early_cta_clicked", {
        location,
        has_referral: !!refCode,
        ref_code: refCode || null,
        page: "/early",
      });
    }
  } catch (_) {
    // Silently ignore — analytics must never break UX
  }
}

function CountdownPill({ value, label, testid }) {
  return (
    <div className="flex flex-col items-center justify-center bg-[#0B1220] border border-[#0052FF]/30 rounded-xl px-3 py-2 min-w-[68px]">
      <span
        className="font-display text-2xl sm:text-3xl font-bold text-[#F1F5F9] tabular-nums leading-none"
        data-testid={testid}
      >
        {String(value).padStart(2, "0")}
      </span>
      <span className="text-[9px] uppercase tracking-[0.18em] text-[#94A3B8] mt-1.5">{label}</span>
    </div>
  );
}

export default function EarlyAdopter() {
  const [params] = useSearchParams();
  const refCode = (params.get("ref") || "").toUpperCase().trim();
  const [inviter, setInviter] = useState(null); // {valid, inviter_name, trial_days, bonus_days_per_referral}
  const [lookupError, setLookupError] = useState(false);

  const deadline = useMemo(() => getPromoDeadline(), []);
  const { days, hours, minutes, seconds } = useCountdown(deadline);

  useEffect(() => {
    if (!refCode) return;
    let cancelled = false;
    axios
      .get(`${API}/api/referral/lookup`, { params: { code: refCode } })
      .then((res) => {
        if (!cancelled) setInviter(res.data);
      })
      .catch(() => {
        if (!cancelled) setLookupError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [refCode]);

  const registerHref = refCode ? `/register?ref=${refCode}` : "/register";

  // Fire a single named pageview event when /early loads, including the referral
  // context. This makes funnel queries in PostHog cleaner than relying purely
  // on $pageview + URL params parsing.
  useEffect(() => {
    try {
      if (typeof window !== "undefined" && window.posthog && typeof window.posthog.capture === "function") {
        window.posthog.capture("early_page_viewed", {
          has_referral: !!refCode,
          ref_code: refCode || null,
        });
      }
    } catch (_) {
      // analytics must never break UX
    }
    // We only want this to fire once per mount + when the referral resolves.
  }, [refCode]);

  return (
    <div className="min-h-screen bg-[#0B1220] text-[#F1F5F9]" data-testid="early-adopter-page">
      {/* Top nav */}
      <header className="border-b border-[#1E293B]/60 bg-[#0B1220]/80 backdrop-blur-md sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="early-adopter-logo">
            <FinlyLogo size={36} rounded="22%" />
            <div className="leading-tight">
              <div className="font-display text-lg font-bold">Finly+</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#94A3B8]">Early Adopter</div>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/login" className="hidden sm:block">
              <Button
                variant="ghost"
                className="text-[#94A3B8] hover:text-[#F1F5F9] hover:bg-[#1E293B]"
                data-testid="early-adopter-login-btn"
              >
                Iniciar sesión
              </Button>
            </Link>
            <Link to={registerHref}>
              <Button
                className="bg-[#0052FF] hover:bg-[#0044d6] text-white"
                data-testid="early-adopter-nav-cta"
                onClick={() => trackCta("nav", refCode)}
              >
                Empezar gratis
                <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-60 pointer-events-none"
          style={{
            background:
              "radial-gradient(60% 50% at 20% 0%, rgba(0,82,255,0.18) 0%, rgba(11,18,32,0) 70%), radial-gradient(40% 35% at 90% 10%, rgba(0,201,125,0.10) 0%, rgba(11,18,32,0) 70%)",
          }}
        />
        <div className="relative max-w-6xl mx-auto px-6 pt-12 sm:pt-16 pb-10 grid lg:grid-cols-[1.15fr_1fr] gap-10 items-center">
          <div>
            <div className="mb-6">
              <FinlyLogoMark size={110} className="drop-shadow-2xl" />
            </div>
            {/* Inviter chip */}
            {inviter && inviter.valid && (
              <div
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#00C97D]/30 bg-[#00C97D]/8 mb-5"
                data-testid="inviter-chip"
              >
                <Gift className="w-3.5 h-3.5 text-[#00C97D]" />
                <span className="text-[12px] text-[#A7F3D0]">
                  Te invita <span className="font-semibold text-[#00C97D]" data-testid="inviter-name">{inviter.inviter_name}</span>
                  {" · "}código <span className="font-mono text-[#F1F5F9]">{refCode}</span> aplicado
                </span>
              </div>
            )}
            {!inviter && refCode && !lookupError && (
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#1E293B] bg-[#131C2E] mb-5">
                <span className="text-[12px] text-[#64748B]">Validando código…</span>
              </div>
            )}
            {refCode && (lookupError || (inviter && !inviter.valid)) && (
              <div
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-amber-500/30 bg-amber-500/10 mb-5"
                data-testid="inviter-invalid"
              >
                <span className="text-[12px] text-amber-300">
                  Código <span className="font-mono">{refCode}</span> no válido — puedes registrarte sin código.
                </span>
              </div>
            )}

            <Badge
              className="bg-[#0052FF]/15 text-[#60A5FA] border border-[#0052FF]/30 hover:bg-[#0052FF]/15 mb-4"
              data-testid="early-adopter-badge"
            >
              <Sparkles className="w-3 h-3 mr-1" /> Acceso Early Adopter
            </Badge>

            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight">
              Tu dinero,
              <br />
              <span className="text-[#0052FF]">en una.</span>
              <span className="text-[#64748B]"> Por menos de un café.</span>
            </h1>

            <p className="mt-5 text-base sm:text-lg text-[#94A3B8] max-w-xl leading-relaxed">
              Premium proyecta tu deuda, ahorra intereses con un{" "}
              <span className="text-[#F1F5F9] font-medium">plan de pago inteligente</span> y entiende tus gastos por voz.
              Hoy lo lanzamos para early adopters al precio más bajo que tendremos.
            </p>

            {/* Price block */}
            <div className="mt-7 flex items-end gap-6">
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-[#94A3B8] mb-1.5">Mensual</div>
                <div className="flex items-baseline gap-1.5">
                  <span className="font-display text-6xl sm:text-7xl font-bold text-[#F1F5F9] leading-none" data-testid="hero-price-monthly">
                    $3
                  </span>
                  <span className="text-lg text-[#94A3B8] font-medium">USD/mes</span>
                </div>
              </div>
              <div className="border-l border-[#1E293B] pl-6">
                <div className="text-[11px] uppercase tracking-[0.22em] text-[#00C97D] mb-1.5 flex items-center gap-1.5">
                  Anual <span className="bg-[#00C97D]/15 text-[#00C97D] rounded px-1.5 py-0.5 text-[10px]">−17%</span>
                </div>
                <div className="flex items-baseline gap-1.5">
                  <span className="font-display text-3xl sm:text-4xl font-bold text-[#F1F5F9] leading-none" data-testid="hero-price-annual">
                    $30
                  </span>
                  <span className="text-sm text-[#94A3B8] font-medium">USD/año</span>
                </div>
              </div>
            </div>

            {/* CTAs */}
            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <Link to={registerHref} className="flex-1 sm:flex-none">
                <Button
                  size="lg"
                  className="bg-[#0052FF] hover:bg-[#0044d6] text-white h-12 px-7 text-base w-full sm:w-auto"
                  data-testid="hero-primary-cta"
                  onClick={() => trackCta("hero_primary", refCode)}
                >
                  Empezar 10 días gratis
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <a href="#comparativa" className="flex-1 sm:flex-none">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-[#1E293B] bg-transparent text-[#CBD5E1] hover:bg-[#131C2E] hover:text-white h-12 px-7 text-base w-full sm:w-auto"
                  data-testid="hero-secondary-cta"
                >
                  Ver Free vs Premium
                </Button>
              </a>
            </div>

            <p className="mt-4 text-xs text-[#64748B]">
              Sin tarjeta para empezar. Cancela cuando quieras. Pagos seguros con Stripe.
            </p>
          </div>

          {/* Countdown panel */}
          <aside
            className="rounded-2xl border border-[#1E293B] bg-gradient-to-br from-[#131C2E] to-[#0E1626] p-6 sm:p-7 shadow-2xl"
            data-testid="countdown-panel"
          >
            <div className="flex items-center gap-2 text-[#60A5FA] text-[11px] uppercase tracking-[0.22em] font-semibold">
              <Clock className="w-3.5 h-3.5" /> Oferta Early Adopter
            </div>
            <div className="mt-2 font-display text-2xl font-bold text-[#F1F5F9] leading-tight">
              Termina en
            </div>
            <div className="mt-4 grid grid-cols-4 gap-2">
              <CountdownPill value={days} label="Días" testid="cd-days" />
              <CountdownPill value={hours} label="Hrs" testid="cd-hours" />
              <CountdownPill value={minutes} label="Min" testid="cd-min" />
              <CountdownPill value={seconds} label="Seg" testid="cd-sec" />
            </div>
            <div className="mt-5 space-y-3 text-sm">
              <div className="flex items-start gap-2.5">
                <Check className="w-4 h-4 text-[#00C97D] mt-0.5 shrink-0" strokeWidth={3} />
                <span className="text-[#CBD5E1]">
                  <span className="text-white font-medium">10 días</span> de Premium gratis al registrarte
                </span>
              </div>
              <div className="flex items-start gap-2.5">
                <Check className="w-4 h-4 text-[#00C97D] mt-0.5 shrink-0" strokeWidth={3} />
                <span className="text-[#CBD5E1]">
                  <span className="text-white font-medium">$3 USD/mes</span> bloqueado de por vida como Early Adopter
                </span>
              </div>
              <div className="flex items-start gap-2.5">
                <Check className="w-4 h-4 text-[#00C97D] mt-0.5 shrink-0" strokeWidth={3} />
                <span className="text-[#CBD5E1]">
                  <span className="text-white font-medium">+30 días</span> extra por cada amig@ que se suscriba con tu código
                </span>
              </div>
            </div>
            <Link to={registerHref}>
              <Button
                className="mt-6 w-full bg-[#00C97D] hover:bg-[#00b06c] text-[#0B1220] font-semibold h-11"
                data-testid="countdown-cta"
                onClick={() => trackCta("countdown", refCode)}
              >
                Quiero mi acceso Early Adopter
              </Button>
            </Link>
          </aside>
        </div>
      </section>

      {/* Comparativa */}
      <section id="comparativa" className="max-w-6xl mx-auto px-6 py-14">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <Badge className="bg-[#131C2E] text-[#94A3B8] border border-[#1E293B] hover:bg-[#131C2E] mb-3">
            Free vs Premium
          </Badge>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#F1F5F9]">
            Lo que desbloqueas con <span className="text-[#0052FF]">Premium</span>
          </h2>
          <p className="mt-3 text-[#94A3B8]">
            Empieza gratis hoy. Si Finly+ te ahorra una hora al mes o un peso de intereses, ya se pagó solo.
          </p>
        </div>

        <div className="rounded-2xl border border-[#1E293B] bg-[#0E1626] overflow-hidden" data-testid="compare-table">
          {/* Header row */}
          <div className="grid grid-cols-[1.4fr_1fr_1fr] bg-[#131C2E] border-b border-[#1E293B]">
            <div className="px-5 py-4 text-[11px] uppercase tracking-[0.18em] text-[#94A3B8] font-semibold">
              Funcionalidad
            </div>
            <div className="px-5 py-4 text-center">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#94A3B8] font-semibold">Free</div>
              <div className="font-display text-xl font-bold text-[#F1F5F9] mt-0.5">$0</div>
            </div>
            <div className="px-5 py-4 text-center bg-[#0052FF]/8 border-l border-[#1E293B]">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#60A5FA] font-semibold">Premium</div>
              <div className="font-display text-xl font-bold text-[#F1F5F9] mt-0.5">$3<span className="text-sm font-normal text-[#94A3B8]">/mes</span></div>
            </div>
          </div>
          {/* Rows */}
          {FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <div
                key={i}
                className={`grid grid-cols-[1.4fr_1fr_1fr] border-b border-[#1E293B] last:border-b-0 ${
                  f.highlight ? "bg-[#0052FF]/4" : ""
                }`}
                data-testid={`compare-row-${i}`}
              >
                <div className="px-5 py-4 flex items-center gap-3 text-[14px] text-[#E2E8F0]">
                  {Icon && <Icon className="w-4 h-4 text-[#64748B] shrink-0" />}
                  <span>
                    {f.label}
                    {f.highlight && (
                      <span className="ml-2 inline-flex items-center text-[10px] uppercase tracking-wider text-[#60A5FA] font-semibold">
                        <Sparkles className="w-3 h-3 mr-0.5" /> nuevo
                      </span>
                    )}
                  </span>
                </div>
                <div className="px-5 py-4 flex items-center justify-center">
                  <FeatureCell value={f.free} />
                </div>
                <div className="px-5 py-4 flex items-center justify-center bg-[#0052FF]/4 border-l border-[#1E293B]">
                  <FeatureCell value={f.premium} />
                </div>
              </div>
            );
          })}
          {/* CTA row */}
          <div className="grid grid-cols-[1.4fr_1fr_1fr] bg-[#131C2E]">
            <div className="px-5 py-5" />
            <div className="px-5 py-5 flex justify-center">
              <Link to={registerHref}>
                <Button
                  variant="outline"
                  className="border-[#1E293B] bg-transparent text-[#CBD5E1] hover:bg-[#0B1220] hover:text-white"
                  data-testid="compare-free-cta"
                  onClick={() => trackCta("compare_free", refCode)}
                >
                  Probar Free
                </Button>
              </Link>
            </div>
            <div className="px-5 py-5 flex justify-center bg-[#0052FF]/8 border-l border-[#1E293B]">
              <Link to={registerHref}>
                <Button className="bg-[#0052FF] hover:bg-[#0044d6] text-white" data-testid="compare-premium-cta" onClick={() => trackCta("compare_premium", refCode)}>
                  Activar Premium <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Trust band */}
      <section className="border-t border-[#1E293B] bg-[#0B1220]">
        <div className="max-w-6xl mx-auto px-6 py-10 grid sm:grid-cols-3 gap-6">
          <div className="flex items-start gap-3">
            <ShieldCheck className="w-5 h-5 text-[#00C97D] mt-0.5" />
            <div>
              <div className="text-sm font-semibold text-[#F1F5F9]">Pagos seguros</div>
              <div className="text-xs text-[#94A3B8] mt-0.5">Procesados por Stripe. Cancela cuando quieras.</div>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Zap className="w-5 h-5 text-[#60A5FA] mt-0.5" />
            <div>
              <div className="text-sm font-semibold text-[#F1F5F9]">Sin tarjeta para empezar</div>
              <div className="text-xs text-[#94A3B8] mt-0.5">10 días Premium gratis, sin necesidad de método de pago.</div>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Gift className="w-5 h-5 text-[#F59E0B] mt-0.5" />
            <div>
              <div className="text-sm font-semibold text-[#F1F5F9]">Recompensa por compartir</div>
              <div className="text-xs text-[#94A3B8] mt-0.5">+30 días Premium por cada amig@ que se suscriba con tu código.</div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-[#1E293B] bg-[#0E1525]" data-testid="early-faq-section">
        <div className="max-w-3xl mx-auto px-6 py-16">
          <div className="text-center mb-10">
            <Badge className="bg-[#1E293B] text-[#94A3B8] border border-[#334155] mb-4">Preguntas frecuentes</Badge>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#F1F5F9] tracking-tight">
              Resolvemos tus dudas
            </h2>
            <p className="text-sm text-[#94A3B8] mt-3">
              Si te queda alguna, escríbenos a <a href="mailto:hola@finly.uno" className="text-[#60A5FA] hover:text-[#93C5FD]">hola@finly.uno</a>.
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-3" data-testid="early-faq-accordion">
            <AccordionItem
              value="cobro"
              className="border border-[#1E293B] rounded-xl bg-[#0B1220] px-5 data-[state=open]:border-[#334155] transition-colors"
              data-testid="faq-item-cobro"
            >
              <AccordionTrigger className="text-[#F1F5F9] hover:no-underline py-5 text-base font-semibold">
                ¿Me cobran al registrarme?
              </AccordionTrigger>
              <AccordionContent className="text-sm text-[#CBD5E1] leading-relaxed pb-5">
                No. El registro es 100% gratis y no pedimos tarjeta. Te damos <strong className="text-[#F1F5F9]">10 días de Premium gratis</strong> para
                que pruebes todo (voz, OCR, plan de pago inteligente). Al final del trial, si no activas suscripción, tu cuenta se
                queda en plan Free — sin cargos sorpresa, sin tarjeta en archivo.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem
              value="trial"
              className="border border-[#1E293B] rounded-xl bg-[#0B1220] px-5 data-[state=open]:border-[#334155] transition-colors"
              data-testid="faq-item-trial"
            >
              <AccordionTrigger className="text-[#F1F5F9] hover:no-underline py-5 text-base font-semibold">
                ¿Qué pasa cuando termina mi prueba de 10 días?
              </AccordionTrigger>
              <AccordionContent className="text-sm text-[#CBD5E1] leading-relaxed pb-5">
                Tu cuenta y todas tus transacciones siguen ahí — nunca se borran. Simplemente las funciones Premium (OCR de tickets,
                plan de pago inteligente, voz ilimitada) se bloquean hasta que actives una suscripción. Te avisamos por email un
                par de días antes para que no te tome por sorpresa.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem
              value="cancelar"
              className="border border-[#1E293B] rounded-xl bg-[#0B1220] px-5 data-[state=open]:border-[#334155] transition-colors"
              data-testid="faq-item-cancelar"
            >
              <AccordionTrigger className="text-[#F1F5F9] hover:no-underline py-5 text-base font-semibold">
                ¿Cómo cancelo mi suscripción?
              </AccordionTrigger>
              <AccordionContent className="text-sm text-[#CBD5E1] leading-relaxed pb-5">
                Desde la app, en <em>Configuración → Suscripción</em>, en un click. No hay llamadas, ni formularios, ni "espera 7
                días hábiles". Mantienes acceso Premium hasta el final del periodo que ya pagaste, y después tu cuenta pasa a Free.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem
              value="seguridad"
              className="border border-[#1E293B] rounded-xl bg-[#0B1220] px-5 data-[state=open]:border-[#334155] transition-colors"
              data-testid="faq-item-seguridad"
            >
              <AccordionTrigger className="text-[#F1F5F9] hover:no-underline py-5 text-base font-semibold">
                ¿Es seguro? ¿Se conecta a mi banco?
              </AccordionTrigger>
              <AccordionContent className="text-sm text-[#CBD5E1] leading-relaxed pb-5">
                <strong className="text-[#F1F5F9]">No nos conectamos a tu banco</strong> — tú decides qué registrar. Las contraseñas se guardan con
                hash bcrypt, las comunicaciones viajan por HTTPS y los pagos los procesa Stripe (nunca vemos tu tarjeta). Puedes
                exportar o borrar tus datos cuando quieras desde la app.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem
              value="precio"
              className="border border-[#1E293B] rounded-xl bg-[#0B1220] px-5 data-[state=open]:border-[#334155] transition-colors"
              data-testid="faq-item-precio"
            >
              <AccordionTrigger className="text-[#F1F5F9] hover:no-underline py-5 text-base font-semibold">
                ¿Subirá el precio después del lanzamiento?
              </AccordionTrigger>
              <AccordionContent className="text-sm text-[#CBD5E1] leading-relaxed pb-5">
                Sí — el precio Early Adopter (USD&nbsp;$3/mes o USD&nbsp;$30/año) sólo está disponible este mes. Si te suscribes
                ahora, <strong className="text-[#F1F5F9]">conservas ese precio mientras tu suscripción esté activa</strong>, aunque después subamos a tarifa regular.
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          <div className="mt-10 text-center">
            <Link to={registerHref}>
              <Button
                size="lg"
                className="bg-[#00C97D] hover:bg-[#00B86D] text-[#0B1220] font-semibold px-8"
                data-testid="faq-cta-register"
                onClick={() => trackCta("faq", refCode)}
              >
                Crear cuenta gratis · 10 días Premium
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer mini */}
      <footer className="border-t border-[#1E293B] bg-[#0B1220]">
        <div className="max-w-6xl mx-auto px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-[#64748B]">
          <div>© {new Date().getFullYear()} Finly+ · Tu dinero, en una.</div>
          <div className="flex items-center gap-4">
            <Link to="/terminos" className="hover:text-[#CBD5E1]" data-testid="footer-link-terms">
              Términos
            </Link>
            <Link to="/privacidad" className="hover:text-[#CBD5E1]" data-testid="footer-link-privacy">
              Privacidad
            </Link>
            <Link to="/login" className="hover:text-[#CBD5E1]" data-testid="footer-login">
              Iniciar sesión
            </Link>
            <Link to={registerHref} className="text-[#60A5FA] hover:text-[#93C5FD]" data-testid="footer-register">
              Crear cuenta
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
